import { useState, type ReactNode } from 'react';
import { Num, T, Var, useGT } from 'gt-react';
import { DownloadSimpleIcon } from '@phosphor-icons/react';

import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FileDropzone } from '../../item-master/components/FileDropzone';
import { downloadTextFile } from '../utils/downloadTextFile';
import { RESULTS_ACCEPT, validateResultsFile } from './resultsFile';
import {
  corporateLine2,
  corporateTitle,
  deriveCorporateUpload,
  sapsDayText,
  leftOutItemsCsv,
  type CorporateLoad,
  type CorporateProblem,
  type CorporateSelection,
  type DivisionUpload,
  type LeftOutRow,
  type LoadProblem,
  type RejectedFile,
  type UnpricedLoad,
} from './rockwellCorporate';
import { UploadPreview } from './UploadPreview';
import { useCorporateFiles } from './useCorporateFiles';

type OkUpload = Extract<DivisionUpload, { status: 'ok' }>;

/** A division shown as its AS400 branch: `+` is W+. */
function branchOf(division: string): string {
  return `W${division}`;
}

/**
 * Upload Corporate: one division's Rockwell corporate agreements from ALL
 * FIXED SAPS, each priced off the Rockwell Price Book Template in effect on
 * its fixed valuation date, as the AS400 upload CSV. One file is one division.
 * A stopgap for the pricing team; the conversion runs in the
 * browser and nothing is stored.
 */
export function CorporateUploadPanel() {
  const files = useCorporateFiles();
  const [selection, setSelection] = useState<CorporateSelection>({
    division: null,
  });
  const view = deriveCorporateUpload({
    saps: files.saps?.sheet ?? null,
    priceBooks: files.priceBooks,
    selection,
  });
  const upload = view.result?.status === 'ok' ? view.result : null;

  return (
    <div className="flex flex-col gap-3 pt-3 pb-[50vh]">
      <Card className="flex flex-col gap-3 p-3">
        <PanelHeading
          csv={upload?.csv ?? null}
          division={upload ? view.division : null}
        />
        <CorporateDropzone
          isReading={files.isReading}
          onFiles={(dropped) => void files.readFiles(dropped)}
        />
        <RejectedFiles files={files.rejected} />
        <CorporatePicks
          sapsFileName={files.saps?.fileName ?? null}
          priceBookNames={files.priceBooks.map(({ fileName }) => fileName)}
          divisions={view.divisions}
          division={view.division}
          onDivision={(division) => setSelection({ division })}
        />
        <UndatedPriceBooks fileNames={view.undatedPriceBooks} />
        <CorporateOutcome
          hasSaps={files.saps !== null}
          hasPriceBooks={files.priceBooks.length > 0}
          divisionCount={view.divisions.length}
          result={view.result}
        />
      </Card>
      {upload ? <CappedPreview lines={upload.lines} /> : null}
    </div>
  );
}

/** Lines the preview grid holds; a division can run to 250k+ lines. */
const PREVIEW_LINES = 5000;

function CappedPreview({ lines }: { lines: OkUpload['lines'] }) {
  return (
    <div className="flex flex-col gap-1">
      {lines.length > PREVIEW_LINES ? (
        <p className="text-muted-foreground text-xs">
          <T context="Upload Corporate: the preview grid shows only the first lines">
            Showing the first <Num>{PREVIEW_LINES}</Num> of{' '}
            <Num>{lines.length}</Num> lines; the download has them all.
          </T>
        </p>
      ) : null}
      <UploadPreview lines={lines.slice(0, PREVIEW_LINES)} />
    </div>
  );
}

function PanelHeading({
  csv,
  division,
}: {
  csv: string | null;
  division: string | null;
}) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <h2 className="text-sm font-medium">
          <T context="heading of the Rockwell corporate upload panel">
            Upload Corporate
          </T>
        </h2>
        <p className="text-muted-foreground text-xs">
          <T context="explanation under the Upload Corporate heading">
            Drop ALL FIXED SAPS and the Rockwell Price Book Templates. Each
            upload is one division; each SPA is priced from the book covering
            its Fixed val.date, one line per item its rules price.
          </T>
        </p>
      </div>
      {csv !== null && division !== null ? (
        <Button
          disabled={csv === ''}
          onClick={() =>
            downloadTextFile('UPLOAD.csv', 'text/csv;charset=utf-8', csv)
          }
        >
          <DownloadSimpleIcon className="h-4 w-4" />
          <T context="button that downloads one division's corporate upload CSV; the value is a branch such as W+">
            Download <Var>{branchOf(division)}</Var> CSV
          </T>
        </Button>
      ) : null}
    </div>
  );
}

function CorporateDropzone({
  isReading,
  onFiles,
}: {
  isReading: boolean;
  onFiles: (files: File[]) => void;
}) {
  const gt = useGT();
  return (
    <FileDropzone
      className="px-3 py-4"
      accept={RESULTS_ACCEPT}
      disabled={isReading}
      prompt={
        isReading ? (
          <T context="Upload Corporate drop area while files are read">
            Reading… large price books take a few seconds each.
          </T>
        ) : (
          <T context="instruction inside the Upload Corporate drop area">
            Drop ALL FIXED SAPS.xlsx and the price book templates here.
          </T>
        )
      }
      validate={(file) => Promise.resolve(validateResultsFile(file, gt))}
      multiple
      onFilesAccepted={onFiles}
    />
  );
}

function RejectedFiles({ files }: { files: RejectedFile[] }) {
  if (files.length === 0) return null;
  return (
    <AlertContainer variant="destructive">
      <AlertTitle>
        <T context="error title for files Upload Corporate could not use">
          These files were not used
        </T>
      </AlertTitle>
      <AlertDescription>
        <ul className="list-disc pl-4">
          {files.map((file) => (
            <li key={file.fileName}>
              <RejectionText file={file} />
            </li>
          ))}
        </ul>
      </AlertDescription>
    </AlertContainer>
  );
}

/** One rejected file and exactly why: unreadable, or the columns it lacks. */
function RejectionText({ file }: { file: RejectedFile }) {
  if (file.reason === 'unreadable') {
    return (
      <T context="Upload Corporate: a dropped file could not be read, with the reader's error">
        <Var>{file.fileName}</Var> could not be read: <Var>{file.message}</Var>.
        If it is open in Excel, close it (or save it) and drop it again.
      </T>
    );
  }
  if (file.reason === 'unrecognised') {
    return (
      <T context="Upload Corporate: a dropped file matches neither ALL FIXED SAPS nor a price book template">
        <Var>{file.fileName}</Var> is neither ALL FIXED SAPS nor a price book
        template. Its header row (row <Var>{file.headerRowNumber}</Var>) starts{' '}
        <Var>{file.headers.join(', ') || '(empty)'}</Var>.
      </T>
    );
  }
  const missing = file.missing.join(', ');
  return file.closest === 'saps' ? (
    <T context="Upload Corporate: a dropped file looks like ALL FIXED SAPS but lacks columns">
      <Var>{file.fileName}</Var> looks like ALL FIXED SAPS, but its header row
      (row <Var>{file.headerRowNumber}</Var>) is missing: <Var>{missing}</Var>.
    </T>
  ) : (
    <T context="Upload Corporate: a dropped file looks like a price book template but lacks columns">
      <Var>{file.fileName}</Var> looks like a price book template, but its
      header row (row <Var>{file.headerRowNumber}</Var>) is missing:{' '}
      <Var>{missing}</Var>.
    </T>
  );
}

function CorporatePicks({
  sapsFileName,
  priceBookNames,
  divisions,
  division,
  onDivision,
}: {
  sapsFileName: string | null;
  priceBookNames: string[];
  divisions: string[];
  division: string | null;
  onDivision: (division: string) => void;
}) {
  const gt = useGT();
  return (
    <div className="grid gap-3 text-sm md:grid-cols-3">
      <Field label={<T context="Upload Corporate: the SAPS file">SAPS file</T>}>
        <span className="truncate">{sapsFileName ?? '—'}</span>
      </Field>
      <Field
        label={
          <T context="Upload Corporate: the division (AS400 branch) to upload">
            Division
          </T>
        }
      >
        <Picker
          value={division}
          options={divisions.map((code) => ({
            value: code,
            label: branchOf(code),
          }))}
          placeholder={gt('Choose a division', {
            context: 'placeholder of the Upload Corporate division pick',
          })}
          onChange={onDivision}
        />
      </Field>
      <Field
        label={
          <T context="Upload Corporate: the price books dropped; each SPA uses the one covering its Fixed val.date">
            Price books
          </T>
        }
      >
        <span className="truncate" title={priceBookNames.join('\n')}>
          {priceBookNames.length > 0 ? priceBookNames.join(', ') : '—'}
        </span>
      </Field>
    </div>
  );
}

/** What stops the upload, what it still needs, or what it wrote. */
function CorporateOutcome({
  hasSaps,
  hasPriceBooks,
  divisionCount,
  result,
}: {
  hasSaps: boolean;
  hasPriceBooks: boolean;
  divisionCount: number;
  result: DivisionUpload | null;
}) {
  if (result?.status === 'invalid') {
    return <Problems problems={result.problems} />;
  }
  if (result) return <UploadSummary upload={result} />;
  if (!hasSaps) return null;
  if (divisionCount === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        <T context="Upload Corporate: no SAPS row has a Cust Name">
          No row of ALL FIXED SAPS has a Cust Name, so there is nothing to load.
        </T>
      </p>
    );
  }
  if (!hasPriceBooks) {
    return (
      <p className="text-muted-foreground text-sm">
        <T context="Upload Corporate: waiting for a price book file">
          Drop a Rockwell Price Book Template to price the upload.
        </T>
      </p>
    );
  }
  return null;
}

function loadName(load: CorporateLoad): string {
  return `${load.customer} · ${load.loadTo}`;
}

/** Price books whose file name says no dates: no SPA can be priced from them. */
function UndatedPriceBooks({ fileNames }: { fileNames: string[] }) {
  if (fileNames.length === 0) return null;
  return (
    <p className="text-destructive text-sm">
      <T context="Upload Corporate: dropped price books whose file name has no date range">
        <Var>{fileNames.join(', ')}</Var>: no “M-D-YYYY to M-D-YYYY” dates in
        the name, so no SPA is priced from it. Keep the template’s name as
        Rockwell sends it.
      </T>
    </p>
  );
}

/** The division's SPAs no dropped price book covers: left out of this file. */
function UnpricedLoads({ loads }: { loads: UnpricedLoad[] }) {
  if (loads.length === 0) return null;
  return (
    <div className="flex flex-col gap-1 text-amber-700 dark:text-amber-500">
      <p>
        <T context="Upload Corporate: SPAs left out because their price book was not dropped">
          Not in this file — no dropped price book covers their Fixed val.date.
          Drop it and they join the upload:
        </T>
      </p>
      <ul className="list-disc pl-4 text-xs">
        {loads.map(({ load, pricingDay }) => (
          <li key={loadName(load)}>
            <T context="Upload Corporate: an SPA whose price book was not dropped, with its Fixed val.date">
              <Var>{loadName(load)}</Var>, fixed{' '}
              <Var>{pricingDay ? sapsDayText(pricingDay) : '—'}</Var>
            </T>
          </li>
        ))}
      </ul>
    </div>
  );
}

function UploadSummary({ upload }: { upload: OkUpload }) {
  return (
    <div className="flex flex-col gap-2 text-sm">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <T context="Upload Corporate summary column: LOAD NUM (column A)">
                Load
              </T>
            </TableHead>
            <TableHead>
              <T context="Upload Corporate summary column: AS400 TITLE">
                Title
              </T>
            </TableHead>
            <TableHead>
              <T context="Upload Corporate summary column: LOADED TO">
                Load to
              </T>
            </TableHead>
            <TableHead>
              <T context="Upload Corporate summary column: AS400 LINE2">
                Line 2
              </T>
            </TableHead>
            <TableHead>
              <T context="Upload Corporate summary column: the price book this SPA's Fixed val.date picked">
                Price book
              </T>
            </TableHead>
            <TableHead className="text-right">
              <T context="Upload Corporate summary column: lines written">
                Lines
              </T>
            </TableHead>
            <TableHead className="text-right">
              <T context="Upload Corporate summary column: price-book items no rule prices">
                No rule
              </T>
            </TableHead>
            <TableHead className="text-right">
              <T context="Upload Corporate summary column: priced items with no list price">
                No list price
              </T>
            </TableHead>
            <TableHead className="text-right">
              <T context="Upload Corporate summary column: priced items with no AS400 item">
                No AS400 item
              </T>
            </TableHead>
            <TableHead className="text-right">
              <T context="Upload Corporate summary column: items left out for a margin of 0 or less">
                Margin ≤ 0
              </T>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {upload.loads.map((summary) => (
            <TableRow key={loadName(summary.load)}>
              <TableCell>
                <Num>{summary.loadNumber}</Num>
              </TableCell>
              <TableCell>{corporateTitle(summary.load.customer)}</TableCell>
              <TableCell>{summary.load.loadTo}</TableCell>
              <TableCell className="font-mono whitespace-pre">
                {corporateLine2(summary.load.division)}
              </TableCell>
              <TableCell
                className="max-w-64 truncate"
                title={summary.priceBookName}
              >
                {summary.priceBookName}
              </TableCell>
              <TableCell className="text-right">
                <Num>{summary.lines}</Num>
              </TableCell>
              <TableCell className="text-right">
                <Num>{summary.unmatched}</Num>
              </TableCell>
              <TableCell className="text-right">
                <Num>{summary.unpriced}</Num>
              </TableCell>
              <TableCell className="text-right">
                <Num>{summary.noItem}</Num>
              </TableCell>
              <TableCell className="text-right">
                <Num>{summary.lowMargin}</Num>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {upload.loads.length === 0 ? (
        <p>
          <T context="Upload Corporate: no SPA of this division is covered by a dropped price book">
            No SPA of this division has a Fixed val.date a dropped price book
            covers.
          </T>
        </p>
      ) : null}
      <LeftOut items={upload.leftOut} />
      <UnpricedLoads loads={upload.unpricedLoads} />
    </div>
  );
}

/** Items shown in the left-out list; the download and the count cover them all. */
const LEFT_OUT_SHOWN = 500;

/** Priced items left out (no AS400 item, or no margin), listed so they can be fixed. */
function LeftOut({ items }: { items: LeftOutRow[] }) {
  if (items.length === 0) return null;
  return (
    <details className="text-amber-700 dark:text-amber-500">
      <summary className="cursor-pointer">
        <T context="Upload Corporate warning: priced items left out for no AS400 item or a margin of 0 or less">
          <Num>{items.length}</Num> priced items are left out: no AS400 item in
          the price book (blank or #N/A), a margin of 0 or less, or an Excel
          error in a column — AS400 rejects an upload holding #N/A.
        </T>
      </summary>
      <ul className="mt-1 max-h-48 overflow-y-auto font-mono text-xs">
        {items
          .slice(0, LEFT_OUT_SHOWN)
          .map(({ load, sheetRow, catalog, reason, sell, cost }) => (
            <li key={`${loadName(load)}:${sheetRow}`}>
              {reason === 'margin' ? (
                <T context="Upload Corporate: one item left out for a margin of 0 or less, with its prices">
                  <Var>{loadName(load)}</Var> — row <Var>{sheetRow}</Var>:{' '}
                  <Var>{catalog}</Var>, sell <Var>{sell}</Var> ≤ cost{' '}
                  <Var>{cost}</Var>
                </T>
              ) : reason === 'error-value' ? (
                <T context="Upload Corporate: one item left out because a column holds an Excel error such as #N/A">
                  <Var>{loadName(load)}</Var> — row <Var>{sheetRow}</Var>:{' '}
                  <Var>{catalog}</Var>, an Excel error (#N/A…) in a column
                </T>
              ) : (
                <T context="Upload Corporate: one price-book item left out for no AS400 item, with its load and Excel row">
                  <Var>{loadName(load)}</Var> — row <Var>{sheetRow}</Var>:{' '}
                  <Var>{catalog}</Var>, no AS400 item
                </T>
              )}
            </li>
          ))}
      </ul>
      <Button
        variant="outline"
        size="sm"
        className="mt-1"
        onClick={() =>
          downloadTextFile(
            'LEFT OUT ITEMS.csv',
            'text/csv;charset=utf-8',
            leftOutItemsCsv(items),
          )
        }
      >
        <DownloadSimpleIcon className="h-4 w-4" />
        <T context="button that downloads every priced item left out of the corporate upload">
          Download Left-Out Items
        </T>
      </Button>
    </details>
  );
}

function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="text-muted-foreground w-24 shrink-0">{label}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function Picker({
  value,
  options,
  placeholder,
  onChange,
}: {
  value: string | null;
  options: { value: string; label: string }[];
  placeholder: string;
  onChange: (value: string) => void;
}) {
  return (
    <Select
      value={value ?? ''}
      onValueChange={(next) => {
        if (typeof next === 'string' && next !== '') onChange(next);
      }}
    >
      <SelectTrigger className="h-8 w-full">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

type ProblemOf<K extends CorporateProblem['kind']> = Extract<
  CorporateProblem,
  { kind: K }
>;

/** One line per problem kind, so each stays a plain sentence. */
const PROBLEM_TEXT: {
  [K in CorporateProblem['kind']]: (problem: ProblemOf<K>) => ReactNode;
} = {
  'missing-headers': (problem) =>
    problem.file === 'saps' ? (
      <T context="Upload Corporate problem: SAPS lacks columns">
        ALL FIXED SAPS is missing columns:{' '}
        <Var>{problem.headers.join(', ')}</Var>
      </T>
    ) : (
      <T context="Upload Corporate problem: price book lacks columns">
        The price book is missing columns:{' '}
        <Var>{problem.headers.join(', ')}</Var>
      </T>
    ),
  'no-rows': (problem) => (
    <T context="Upload Corporate problem: no SAPS rows for the pick">
      No SAPS rows for <Var>{problem.customer}</Var> loaded to{' '}
      <Var>{problem.loadTo}</Var>
    </T>
  ),
  'bad-division': (problem) => (
    <T context="Upload Corporate problem: DIVISION is not a known branch">
      DIVISION <Var>{problem.value}</Var> is not a branch (+, B or J)
    </T>
  ),
  'error-value': (problem) => (
    <T context="Upload Corporate problem: an Excel error such as #N/A in a SAPS field every line carries">
      <Var>{problem.header}</Var> is <Var>{problem.value}</Var>; AS400 rejects
      an upload holding an Excel error. Fix it in ALL FIXED SAPS.
    </T>
  ),
  'ambiguous-price-book': (problem) => (
    <T context="Upload Corporate problem: two dropped price books cover the same Fixed val.date">
      Fixed val.date <Var>{problem.day}</Var> falls in more than one dropped
      price book (<Var>{problem.priceBookNames.join(', ')}</Var>). Drop only one
      of them.
    </T>
  ),
  'bad-load-target': (problem) => (
    <T context="Upload Corporate problem: LOADED TO is not A-… or G-…">
      LOADED TO <Var>{problem.loadTo}</Var> is neither an account (A-…) nor a
      group (G-…)
    </T>
  ),
  'mixed-agreement': (problem) => (
    <T context="Upload Corporate problem: rows disagree on a header field">
      The customer’s rows disagree on <Var>{problem.header}</Var>:{' '}
      <Var>{problem.values.join(', ')}</Var>
    </T>
  ),
  'bad-date': (problem) => (
    <T context="Upload Corporate problem: unreadable date">
      <Var>{problem.header}</Var> is not a date: <Var>{problem.value}</Var>
    </T>
  ),
  'bad-rate': (problem) => (
    <T context="Upload Corporate problem: unreadable rate">
      Row <Var>{problem.sheetRow}</Var>: <Var>{problem.header}</Var> is not a
      rate: <Var>{problem.value}</Var>
    </T>
  ),
  'no-rule-key': (problem) => (
    <T context="Upload Corporate problem: row has neither catalog nor price group">
      Row <Var>{problem.sheetRow}</Var> has no Catalog No or Price Group
    </T>
  ),
  'conflicting-rule': (problem) => (
    <T context="Upload Corporate problem: two different rates for one key">
      Row <Var>{problem.sheetRow}</Var> gives <Var>{problem.key}</Var> different
      rates than an earlier row
    </T>
  ),
};

function problemText<K extends CorporateProblem['kind']>(
  problem: ProblemOf<K>,
): ReactNode {
  const render: (problem: ProblemOf<K>) => ReactNode =
    PROBLEM_TEXT[problem.kind];
  return render(problem);
}

/** Shown in place of the summary; the first few, since one cause repeats. */
const PROBLEMS_SHOWN = 20;

function Problems({ problems }: { problems: LoadProblem[] }) {
  return (
    <AlertContainer variant="destructive">
      <AlertTitle>
        <T context="Upload Corporate: title over the problems list">
          Can’t build the upload (<Num>{problems.length}</Num> problems)
        </T>
      </AlertTitle>
      <AlertDescription>
        <ul className="list-disc pl-4">
          {problems.slice(0, PROBLEMS_SHOWN).map(({ load, problem }) => (
            // No two problems are alike, so the problem itself is its key.
            <li key={JSON.stringify([load, problem])}>
              <Var>{loadName(load)}</Var>: {problemText(problem)}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </AlertContainer>
  );
}
